import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { autobind, debounce } from 'core-decorators'

import { observable } from 'mobx'
import { observer } from 'mobx-react'

import FormSection from '../../shared/components/FormSection'
import FormGroup from '../../shared/components/FormGroup'
import TextInput from '../../shared/components/TextInput'
import SelectInput from '../../shared/components/SelectInput'

const geocodable = [
  'name',
  'street',
  'number',
  'zip',
  'location',
  'region',
  'latitude',
  'longitude',
]

const geocodingStatusTexts = {
  ZERO_RESULTS: 'Für die angegebenen Daten konnten keine Daten gefunden werden.',
  OVER_QUERY_LIMIT: 'Kontingent überschritten.',
  REQUEST_DENIED: 'Die Anfrage wurde vom Server abgelehnt.',
  INVALID_REQUEST: 'Irgendwas an der Anfrage war inkorrekt.',
}

@observer
class AddressFormSection extends Component {

  /* eslint-disable react/sort-comp */
  @observable geocodingFormattedAddress = null

  @observable geocodingError = null
  /* eslint-enable react/sort-comp */

  static propTypes = {
    address: PropTypes.shape({
      latitude: PropTypes.string,
      location: PropTypes.string,
      longitude: PropTypes.string,
      region: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
      ]),
      street: PropTypes.string,
      zip: PropTypes.string,
    }),
    dirtyFields: PropTypes.object,
    fieldSpecs: PropTypes.object,
    geocoder: PropTypes.shape({
      geocodeAddress: PropTypes.func.isRequired
    }),
    name: PropTypes.string,
    onChange: PropTypes.func,
    onChangeMultiple: PropTypes.func,
    readOnly: PropTypes.bool,
    show: PropTypes.bool,
    validationObj: PropTypes.object,
  }

  constructor(props) {
    super(props)
    this.geocoded = {}
    this.geocode()
  }

  @debounce(500)
  geocode(field) {

    field = field && field.replace('address.', '')

    if (this.props.geocoder && !(this.props.geocoder instanceof Error)) {

      const currentAddress = {
        name: this.props.name,
        address: (field && field === 'name')
          ? {}
          : {
            ...Object.keys(this.props.address)
              .filter((key) => {
                return !(
                  key in this.geocoded
                  && this.geocoded[key]
                  && `${this.geocoded[key]}`.trim()
                )
              })
              .reduce((memo, key) => {
                memo[key] = this.props.address[key]
                return memo
              }, {})
          }
      }

      this.geocoded = currentAddress

      this.props.geocoder.geocodeAddress(currentAddress)
        .then((result) => {

          this.geocoded = geocodable
            .reduce((memo, key) => {
              if (field !== key && key in result && result[key]) {

                // prevent geocoding from overwriting the region
                // as google returns a name (string) yet we need an
                // id (number)
                if (key !== 'region') {
                  memo[key] = result[key]
                }
              }
              return memo
            }, {})

          const address = {
            // ...this.props.address,
            ...this.geocoded,
          }

          this.props.onChangeMultiple({ address })

          this.geocodingError = null
          this.geocodingFormattedAddress = result.formatted

        })
        .catch((ex) => {
          if (ex.status) {
            this.geocodingError = geocodingStatusTexts[ex.status]
            this.geocodingFormattedAddress = null
          }
          else {
            console.error(ex)
          }
        })
    }
  }

  @autobind
  handleResetCoords(e) {

    e.preventDefault()
    e.stopPropagation()

    if (this.props.onChangeMultiple) {
      this.props.onChangeMultiple({
        address: {
          latitude: '',
          longitude: '',
          region: '',
          street: '',
          number: '',
          zip: '',
          location: '',
        }
      })
    }
  }

  @autobind
  handleChange(...args) {
    if (this.props.onChange) {
      this.props.onChange(...args)
    }
    this.geocode(args[0].target.name)
  }

  render() {
    const {
      validationObj,
      address,
      name,
      show,

      readOnly,
      // dirtyFields,
      fieldSpecs
    } = this.props

    const addr = address || {}

    const onChange = this.handleChange

    const {
      number,
      latitude,
      location,
      longitude,
      region,
      street,
      zip
    } = addr

    const filterFn = ((districtId) => {
      if (!districtId) {
        return () => true
      }
      return (r) => districtId === r.districtId
    })(this.props.districtId)

    const regions = [
      { title: '-- Bitte auswählen --', id: -1 },
      ...window.APP_CONFIG.list_region.filter(filterFn)
    ]

    return (
      <FormSection
        title="1. Anschrift"
        show={show}
      >

        <TextInput
          name="name"
          value={name}
          isValid={validationObj.name}
          disabled={readOnly}
          onChange={onChange}
          {...fieldSpecs.name}
        />

        <FormGroup
          label={fieldSpecs['address.street'].label}
          info={fieldSpecs['address.street'].info}
          note={fieldSpecs['address.street'].note}
          name="address.street"
          onChange={onChange}
        >
          <div className="col-xs-6">
            <input
              className="form-control"
              id="address.street"
              name="address.street"
              value={street}
              placeholder="Adolf-Wächter-Straße"
              disabled={readOnly}
              type={fieldSpecs['address.street'].type}
              required={fieldSpecs['address.street'].required}
            />
          </div>
          <div className="col-xs-2">
            <input
              type="text"
              className="form-control"
              id="address.number"
              name="address.number"
              value={number}
              disabled={readOnly}
            />
          </div>
        </FormGroup>

        <FormGroup
          label={fieldSpecs['address.zip'].label}
          info={fieldSpecs['address.zip'].info}
          note={fieldSpecs['address.zip'].note}
          name="address.location"
          onChange={onChange}
        >
          <div className="col-xs-2">
            <input
              type="number"
              className="form-control"
              id="address.zip"
              name="address.zip"
              value={zip}
              disabled={readOnly}
            />
          </div>
          <div className="col-xs-6">
            <input
              type="text"
              className="form-control"
              id="address.location"
              name="address.location"
              value={location}
              disabled={readOnly}
            />
          </div>
        </FormGroup>

        <SelectInput
          name="address.region"
          value={region ? parseInt(`${region}`, 10) : -1}
          options={regions}
          disabled={readOnly}
          onChange={onChange}
          {...fieldSpecs['address.region']}
        />

        <FormGroup
          label={fieldSpecs['address.latitude'].label}
          info={fieldSpecs['address.latitude'].info}
          note={fieldSpecs['address.latitude'].note}
          name="address.latitude"
          onChange={onChange}
        >
          <div className="col-xs-8 no-padding">
            <div className="clearfix">
              <div className="col-xs-4">
                <input
                  type="number"
                  className="form-control"
                  id="address.latitude"
                  name="address.latitude"
                  value={latitude}
                  disabled={readOnly}
                />
              </div>
              <div className="col-xs-4">
                <input
                  type="number"
                  className="form-control"
                  id="address.longitude"
                  name="address.longitude"
                  value={longitude}
                  disabled={readOnly}
                />
              </div>
            </div>
            {!readOnly
              ? (
                <div>
                  {/*
              <div
                style={{ padding: '0 15px' }}
                className="help-block clearfix"
              >
                In der Regel werden die Koordinaten automatisch vom System, auf Basis der
                eingegebenen Adresse darüber, ermittlet. Sollte dies nicht der Fall sein
                oder sollten die Koordinaten falsch sein, haben Sie hier die Möglichkeit
                diese manuell einzugeben.
              </div>

              <div
                style={{ padding: '0 15px' }}
                className="help-block location-info clearfix"
              >

                <div
                  className=""
                >
                  {this.geocodingError
                    ? <div><strong>Die Geokoordinaten konnten nicht festgestellt werden:</strong> {this.geocodingError}.</div>
                    : <div><strong>Ermittelte Adresse:</strong> {this.geocodingFormattedAddress}</div>
                  }
                </div>

              </div>
              */}
                  <div
                    style={{ padding: '0 15px' }}
                    className="clearfix"
                  >
                    <button
                      className="btn btn-mini btn-default pull-right"
                      onClick={this.handleResetCoords}
                    >
                      Adressfelder zurücksetzen
                    </button>
                  </div>
                </div>
              )
              : null}
          </div>
        </FormGroup>

      </FormSection>
    )
  }
}

export { AddressFormSection as default }



// WEBPACK FOOTER //
// ./src/museum/components/01-AddressFormSection.js